// pages/HomePage.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { whyChooseUsItems } from '../data/constants';
import Button from '../components/ui/Button';
import Section from '../components/layout/Section';
import TrustSignals from '../components/ui/TrustSignals';
import ServiceArea from '../components/ui/ServiceArea';
import { trackCtaClick } from '../utils/tracking';
import img from '../assets/img.jpg';

const services = [
  {
    icon: '🏗️',
    title: 'New Roof Installation',
    desc: 'Custom-built roofing systems designed for Florida\'s climate. Shingle, metal, and flat roof options with manufacturer-backed warranties.',
  },
  {
    icon: '🛠️',
    title: 'Roof Repair & Maintenance',
    desc: 'Fast, reliable repairs for leaks, storm damage, missing shingles, and flashing issues. We respond promptly to protect your home.',
  },
  {
    icon: '🔍',
    title: 'Roof Inspections',
    desc: 'Comprehensive inspections for home buyers, insurance claims, and preventative maintenance. Detailed reports you can trust.',
  },
  {
    icon: '🌪️',
    title: 'Storm Damage Restoration',
    desc: 'Emergency tarping and full restoration after hurricanes and severe weather. We work with your insurance company directly.',
  },
];

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [showVideo] = useState(false);
  return (
    <>
      <div className="pt-20">
        {/* Hero */}
        <div className="py-28 md:py-44 text-white relative overflow-hidden">
          {showVideo ? (
            <video
              className="absolute inset-0 w-full h-full object-cover"
              autoPlay muted loop playsInline
            >
              <source src="/video/Commercial.mov" type="video/mp4" />
            </video>
          ) : (
            <div
              className="absolute inset-0 bg-cover bg-center scale-105"
              style={{ backgroundImage: `url(${img})` }}
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <div className="inline-block px-4 py-1.5 bg-lime-500/20 border border-lime-500/40 rounded-full text-lime-400 text-sm font-semibold mb-6 backdrop-blur-sm">
              Serving Highlands County for 30+ Years
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold mb-6 leading-tight tracking-tight">
              Your Roof.<br />
              <span className="bg-gradient-to-r from-lime-400 to-lime-500 bg-clip-text text-transparent">Our Expertise.</span>
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl mb-10 max-w-2xl mx-auto text-gray-200 font-light">
              Quality craftsmanship and honest service from Avon Park's most trusted roofing team.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={() => { trackCtaClick('/request-quote', 'Get Your Free Estimate'); navigate('/request-quote'); }} variant="primary" className="text-lg px-8 py-4 shadow-lg shadow-lime-500/25">
                Get Your Free Estimate
              </Button>
              <Button onClick={() => { trackCtaClick('tel:18634534555', 'Call Now'); window.location.href = 'tel:18634534555'; }} variant="outline" className="text-lg px-8 py-4 border-white/30 text-white hover:bg-white/10">
                Call (863) 453-4555
              </Button>
            </div>
          </div>
        </div>
      </div>

      <TrustSignals />

      {/* Services */}
      <Section title="What We Do" bg="bg-slate-50">
        <p className="text-center text-lg text-gray-600 mb-14 max-w-2xl mx-auto">
          Complete residential roofing services backed by three decades of hands-on experience.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map(s => (
            <div key={s.title} className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 hover:shadow-lg hover:border-lime-200 hover:-translate-y-1 transition-all duration-300 group">
              <div className="text-4xl mb-4 group-hover:scale-110 transition-transform">{s.icon}</div>
              <h3 className="text-lg font-bold text-slate-800 mb-2">{s.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-12">
          <Button onClick={() => { trackCtaClick('/services', 'View All Services'); navigate('/services'); }} variant="secondary">
            View All Services
          </Button>
        </div>
      </Section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24 bg-slate-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Why Homeowners Choose <span className="text-lime-400">Lemay Roofing</span>
          </h2>
          <p className="text-center text-gray-400 mb-14 max-w-xl mx-auto">
            We're not the biggest roofing company — we're the one your neighbors recommend.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChooseUsItems.map(item => (
              <div key={item.text} className="flex items-start gap-4 p-5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors">
                <span className="text-3xl flex-shrink-0 mt-0.5">{item.icon}</span>
                <p className="text-gray-300 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-14">
            <Button onClick={() => { trackCtaClick('/request-quote', 'Request Your Free Quote Now'); navigate('/request-quote'); }} variant="primary" className="shadow-lg shadow-lime-500/25">
              Request Your Free Quote Now
            </Button>
          </div>
        </div>
      </section>

      <ServiceArea />
    </>
  );
};

export default HomePage;
